import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, CheckCircle, XCircle, Clock, Calendar, User, Users, Trash2, Edit } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddLeaveModal from '../Modals/AddLeaveModal';
import { fetchLeavePersonnel, belongsToPersonnel, LeavePersonnel } from '../../lib/leavePersonnel';

interface LeaveRequest {
    id: string;
    user_id: string | null;
    personnel_name?: string;
    personnel_id?: string | null;
    leave_type: 'annual' | 'sick' | 'casual' | 'maternity' | 'paternity' | 'unpaid' | 'other';
    start_date: string;
    end_date: string;
    days_count: number;
    reason: string;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    approved_by: string;
    created_at: string;
}

const LeaveTracking: React.FC = () => {
    const { user } = useAuth();
    const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
    const [personnelList, setPersonnelList] = useState<LeavePersonnel[]>([]);
    const [personnelSearch, setPersonnelSearch] = useState('');
    const [newPersonnelName, setNewPersonnelName] = useState('');
    const [savingPersonnel, setSavingPersonnel] = useState(false);
    const [personnelError, setPersonnelError] = useState('');
    const [selectedPersonnel, setSelectedPersonnel] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingLeave, setEditingLeave] = useState<LeaveRequest | null>(null);

    useEffect(() => {
        fetchPersonnel();
    }, [user?.id, user?.branchId, user?.role]);

    useEffect(() => {
        fetchLeaves();
    }, [statusFilter]);

    const fetchPersonnel = async () => {
        try {
            setPersonnelError('');
            setPersonnelList(await fetchLeavePersonnel(user?.branchId, user?.role === 'admin'));
        } catch (err) {
            console.error('Error fetching personnel:', err);
            setPersonnelError('Personel listesi yüklenemedi. Lütfen tekrar deneyin.');
        }
    };

    const fetchLeaves = async () => {
        try {
            let query = supabase
                .from('leaves')
                .select('*')
                .order('created_at', { ascending: false });

            if (statusFilter !== 'all') {
                query = query.eq('status', statusFilter);
            }

            const { data, error } = await query;
            if (error) throw error;
            setLeaves(data || []);
        } catch (err) {
            console.error('Error fetching leaves:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddPersonnel = async (event: React.FormEvent) => {
        event.preventDefault();
        const name = newPersonnelName.trim();
        if (!canApprove || !name || savingPersonnel) return;
        setSavingPersonnel(true);
        setPersonnelError('');
        try {
            const { data, error } = await supabase.from('leave_personnel')
                .insert({ name, branch_id: user?.branchId || null }).select('id, name, user_id, branch_id').single();
            if (error) throw error;
            setPersonnelList(current => [...current, data].sort((a, b) => a.name.localeCompare(b.name, 'tr')));
            setNewPersonnelName('');
            setSelectedPersonnel(data.id);
        } catch (err) {
            console.error('Error adding personnel:', err);
            setPersonnelError('Personel eklenemedi. Lütfen tekrar deneyin.');
        } finally {
            setSavingPersonnel(false);
        }
    };

    const handleRemovePersonnel = async (person: LeavePersonnel) => {
        if (!canApprove || savingPersonnel) return;
        if (!await window.appConfirm(`${person.name} personel listesinden kaldırılsın mı? Geçmiş izin kayıtları ve kullanıcı hesabı korunacaktır.`)) return;
        setSavingPersonnel(true);
        setPersonnelError('');
        try {
            const { error } = await supabase.from('leave_personnel').update({ is_active: false })
                .eq('id', person.id).select('id').single();
            if (error) throw error;
            setPersonnelList(current => current.filter(item => item.id !== person.id));
            if (selectedPersonnel === person.id) setSelectedPersonnel(null);
        } catch (err) {
            console.error('Error removing personnel:', err);
            setPersonnelError('Personel kaldırılamadı. Lütfen tekrar deneyin.');
        } finally {
            setSavingPersonnel(false);
        }
    };

    const getPersonnelName = (id: string): string => {
        const person = personnelList.find(p => p.id === id);
        return person?.name || '';
    };

    const handleUpdateStatus = async (id: string, newStatus: 'approved' | 'rejected') => {
        if (!await window.appConfirm(`Bu izin talebini ${newStatus === 'approved' ? 'onaylamak' : 'reddetmek'} istediğinizden emin misiniz?`)) return;

        try {
            const { error } = await supabase
                .from('leaves')
                .update({
                    status: newStatus,
                    approved_by: user?.id,
                    approved_at: new Date().toISOString()
                })
                .eq('id', id);

            if (error) throw error;
            fetchLeaves();
        } catch (err) {
            console.error('Error updating leave status:', err);
            alert('İşlem sırasında bir hata oluştu.');
        }
    };

    const handleDeleteLeave = async (id: string) => {
        if (!await window.appConfirm('Bu izin kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;

        try {
            const { error } = await supabase
                .from('leaves')
                .delete()
                .eq('id', id);

            if (error) throw error;
            fetchLeaves();
        } catch (err) {
            console.error('Error deleting leave:', err);
            alert('Silme işlemi sırasında bir hata oluştu.');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Onaylandı</span>;
            case 'rejected':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Reddedildi</span>;
            case 'pending':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Bekliyor</span>;
            case 'cancelled':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> İptal Edildi</span>;
            default:
                return null;
        }
    };

    const getLeaveTypeLabel = (type: string) => {
        switch (type) {
            case 'annual': return 'Yıllık İzin';
            case 'sick': return 'Hastalık İzni';
            case 'casual': return 'Mazeret İzni';
            case 'maternity': return 'Doğum İzni';
            case 'paternity': return 'Babalık İzni';
            case 'unpaid': return 'Ücretsiz İzin';
            case 'other': return 'Diğer';
            default: return type;
        }
    };

    const filteredLeaves = leaves.filter(leave => {
        const person = personnelList.find(p => p.id === selectedPersonnel);
        if (selectedPersonnel && (!person || !belongsToPersonnel(leave, person))) return false;
        const searchLower = searchTerm.toLowerCase();
        const fullName = leave.personnel_name || '';
        const reason = leave.reason || '';

        return fullName.toLowerCase().includes(searchLower) ||
            reason.toLowerCase().includes(searchLower);
    });

    // Seçili personelin toplam izin günü
    const totalDaysUsed = filteredLeaves.reduce((sum, leave) => {
        if (leave.status === 'approved') {
            return sum + (leave.days_count || 0);
        }
        return sum;
    }, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const canApprove = user?.role === 'admin' || user?.unitType === 'accounting';

    return (
        <div className="flex h-full">
            {/* Sol Sidebar - Personel Listesi */}
            <div className="w-72 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2 mb-3">
                        <Users className="w-5 h-5 text-blue-600" />
                        <h2 className="font-semibold text-gray-900 dark:text-white">Personel Listesi</h2>
                    </div>
                    <input
                        type="text"
                        placeholder="Personel ara..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                        value={personnelSearch}
                        onChange={(e) => setPersonnelSearch(e.target.value)}
                    />
                    {canApprove && (
                        <form onSubmit={handleAddPersonnel} className="mt-3 space-y-2">
                            <input aria-label="Yeni personel adı soyadı" placeholder="Yeni personel adı soyadı" required maxLength={150}
                                value={newPersonnelName} onChange={e => setNewPersonnelName(e.target.value)}
                                className="w-full px-3 py-2 text-sm border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                            <button type="submit" disabled={savingPersonnel || !newPersonnelName.trim()}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
                                <Plus className="w-4 h-4" /> Personel Ekle
                            </button>
                        </form>
                    )}
                    {personnelError && <p role="alert" className="mt-2 text-sm text-red-600">{personnelError}</p>}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {/* Tümü seçeneği */}
                    <button
                        onClick={() => setSelectedPersonnel(null)}
                        className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedPersonnel === null ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-600' : ''
                            }`}
                    >
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white">Tüm Personel</p>
                            <p className="text-xs text-gray-500">{leaves.length} izin kaydı</p>
                        </div>
                    </button>

                    {personnelList.filter(person => person.name.toLocaleLowerCase('tr').includes(personnelSearch.toLocaleLowerCase('tr'))).map((person) => {
                        const personLeaves = leaves.filter(l => belongsToPersonnel(l, person));
                        const personDays = personLeaves.filter(l => l.status === 'approved').reduce((s, l) => s + (l.days_count || 0), 0);

                        return (
                            <div key={person.id} className="flex items-center">
                            <button
                                onClick={() => setSelectedPersonnel(person.id)}
                                className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedPersonnel === person.id ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-600' : ''
                                    }`}
                            >
                                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white truncate">{person.name}</p>
                                    <p className="text-xs text-gray-500">{personLeaves.length} izin • {personDays} gün</p>
                                </div>
                            </button>
                            {canApprove && (
                                <button type="button" disabled={savingPersonnel} onClick={() => handleRemovePersonnel(person)}
                                    aria-label={`${person.name} personelini kaldır`} title="Personeli listeden kaldır"
                                    className="p-2 mr-2 text-gray-400 hover:text-red-600 disabled:opacity-50">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Ana İçerik Alanı */}
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">İzin Takip</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            {selectedPersonnel ? `${getPersonnelName(selectedPersonnel)} - ${filteredLeaves.length} izin kaydı, Toplam ${totalDaysUsed} gün` : 'Personel izin talep ve onay yönetimi'}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                        <Plus className="w-5 h-5" />
                        {canApprove ? 'Personel İzni Gir' : 'Yeni İzin Talebi'}
                    </button>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="İzin ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter className="text-gray-400 w-5 h-5" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">Tüm Durumlar</option>
                            <option value="pending">Bekleyenler</option>
                            <option value="approved">Onaylananlar</option>
                            <option value="rejected">Reddedilenler</option>
                            <option value="cancelled">İptal Edilenler</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {filteredLeaves.length === 0 ? (
                        <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
                            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">İzin talebi bulunamadı</h3>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                {selectedPersonnel ? 'Bu personele ait izin kaydı yok.' : 'Henüz bir izin talebi oluşturulmamış.'}
                            </p>
                        </div>
                    ) : (
                        filteredLeaves.map((leave) => (
                            <div
                                key={leave.id}
                                className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 hover:shadow-lg transition-all duration-200"
                            >
                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="flex items-start gap-4 flex-1">
                                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                            <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {leave.personnel_name || 'Bilinmeyen Kullanıcı'}
                                                </h3>
                                                <div className="flex items-center gap-2">
                                                    {(canApprove || (user?.id === leave.user_id && leave.status === 'pending')) && (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingLeave(leave);
                                                                    setShowAddModal(true);
                                                                }}
                                                                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                                                                title="Düzenle"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteLeave(leave.id)}
                                                                className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                                                                title="Sil"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                    {getStatusBadge(leave.status)}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                <div>
                                                    <p className="text-gray-500 dark:text-gray-400 text-xs">İzin Türü</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">{getLeaveTypeLabel(leave.leave_type)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500 dark:text-gray-400 text-xs">Tarih Aralığı</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {format(new Date(leave.start_date), 'dd MMM yyyy', { locale: tr })} - {format(new Date(leave.end_date), 'dd MMM yyyy', { locale: tr })}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500 dark:text-gray-400 text-xs">Süre</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">{leave.days_count} Gün</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500 dark:text-gray-400 text-xs">Oluşturulma</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {format(new Date(leave.created_at), 'dd MMM HH:mm', { locale: tr })}
                                                    </p>
                                                </div>
                                            </div>

                                            {leave.reason && (
                                                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                                        <span className="font-medium text-gray-900 dark:text-white mr-2">Açıklama:</span>
                                                        {leave.reason}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {canApprove && leave.status === 'pending' && user?.id !== leave.user_id && (
                                        <div className="flex flex-row md:flex-col gap-2 justify-center border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-800 pt-4 md:pt-0 md:pl-6">
                                            <button
                                                onClick={() => handleUpdateStatus(leave.id, 'approved')}
                                                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm whitespace-nowrap"
                                            >
                                                <CheckCircle className="w-4 h-4" />
                                                Onayla
                                            </button>
                                            <button
                                                onClick={() => handleUpdateStatus(leave.id, 'rejected')}
                                                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm whitespace-nowrap"
                                            >
                                                <XCircle className="w-4 h-4" />
                                                Reddet
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <AddLeaveModal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        setEditingLeave(null);
                    }}
                    onSuccess={fetchLeaves}
                    leaveToEdit={editingLeave}
                    personnel={personnelList}
                    selectedPersonnelId={selectedPersonnel}
                />
            </div>
        </div>
    );
};

export default LeaveTracking;
